import { useMemo } from 'react';
import { Button, Pagination } from 'antd';
import { useTranslation } from 'react-i18next';
import printerIcon from '../../../../../../../img/printer.svg';
import linkIcon from '../../../../../../../img/Link.svg';
import { createPaginationConfig } from '@client/utils/table/tableUtils';
import { SaveSearchButton } from '@client/components/SavedSearch';

/**
 * Results header with title, print button, and count display
 * @param {Object} props - Component props
 * @param {Object} props.data - Search results data
 * @param {number} props.data.count - Total number of results
 * @param {string} props.data.query_time - Query execution time
 * @param {Function} props.onPageChange - Page change handler
 * @returns {JSX.Element} Results header
 */
const ResultsHeader = ({ data, onShare, onPageChange }) => {
  const { t } = useTranslation();

  const pageSize = data?.size || 20;
  const totalCount = data?.count || 0;

  const pagination = useMemo(
    () =>
      createPaginationConfig(data, pageSize, t, {
        showSizeChanger: false,
        showQuickJumper: false,
      }),
    [data, pageSize, t]
  );

  const handlePageChange = (page, newPageSize) => {
    if (onPageChange) {
      onPageChange({ page, size: newPageSize });
    }
  };

  return (
    <div className="flex flex-col gap-4 mb-6 sm:mb-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
        <h4 className="normal-case text-lg sm:text-xl font-medium">
          {t('active-substance|Search results')}
        </h4>

        <div className="flex flex-col gap-2 w-full sm:flex-row sm:w-auto sm:gap-2">
          <Button
            type="default"
            variant="outlined"
            size="large"
            className="print-button-with-icon w-full sm:w-auto text-sm sm:text-base py-2.5 px-4 sm:py-2 sm:px-3"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <img src={printerIcon} alt="Print" className="w-4 h-4 print-icon" />
            {t('active-substance|Printable version')}
          </Button>
          <Button
            size="large"
            type="primary"
            onClick={onShare}
            className="w-full sm:w-auto text-sm sm:text-base py-2.5 px-4 sm:py-2 sm:px-3 font-medium"
          >
            <img src={linkIcon} alt="Share" className="w-4 h-4" />
            {t('common|Share results')}
          </Button>
          <SaveSearchButton
            searchType="treatment"
            getCurrentUrl={() => window.location.href}
          />
        </div>
      </div>

      {totalCount > 0 && (
        <div className="flex flex-col gap-4">
          <Pagination
            {...pagination}
            onChange={handlePageChange}
            className="custom-pagination !mt-[0]"
          />
        </div>
      )}
    </div>
  );
};

export default ResultsHeader;
